import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Bell, Send, Zap } from "lucide-react";

const empty = { title: "", body: "", target_role: "all", link: "" };

export default function AdminNotifications() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [subs, setSubs] = useState({ total: 0, by_role: {} });
  const [pushOpen, setPushOpen] = useState(false);
  const [pushForm, setPushForm] = useState({ title: "", body: "", url: "/", target_role: "all" });
  const [sending, setSending] = useState(false);

  const load = async () => {
    const [n, s] = await Promise.all([api.get("/notifications"), api.post("/push/subscriber-count")]);
    setItems(n.data); setSubs(s.data);
  };
  useEffect(() => { load(); }, []);

  const send = async () => {
    if (!form.title || !form.body) return toast.error("Títol i missatge obligatoris");
    try {
      const { data } = await api.post("/notifications", form);
      toast.success(`Creada · ${data.push_delivered || 0}/${data.push_targets || 0} push enviats`);
      setOpen(false); setForm(empty); load();
    } catch { toast.error("Error"); }
  };

  const broadcast = async () => {
    if (!pushForm.title || !pushForm.body) return toast.error("Títol i missatge obligatoris");
    setSending(true);
    try {
      const { data } = await api.post("/push/broadcast", pushForm);
      toast.success(`Push enviat · ${data.delivered}/${data.targets} entregat${data.targets === 1 ? "" : "s"}`);
      setPushOpen(false); setPushForm({ title: "", body: "", url: "/", target_role: "all" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setSending(false); }
  };

  const testPush = async () => {
    try {
      const { data } = await api.post("/push/test");
      toast.success(`Prova enviada · ${data.delivered}/${data.total}`);
    } catch { toast.error("Cal subscriure't primer (activa push al teu dispositiu)"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-notifications">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline text-accent">Push · Comunicació</div>
          <h1 className="mt-2 text-3xl font-heading font-bold tracking-tight">Notificacions</h1>
          <p className="text-sm text-muted-foreground mt-2">
            <strong>{subs.total}</strong> dispositius subscrits · <span className="text-xs">{subs.by_role?.associate || 0} associats · {subs.by_role?.admin || 0} admin</span>
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" className="rounded-full" onClick={testPush} data-testid="notif-test-push-btn">
            <Zap className="w-4 h-4 mr-2" aria-hidden="true" /> Prova (jo)
          </Button>
          <Dialog open={pushOpen} onOpenChange={setPushOpen}>
            <DialogTrigger asChild>
              <Button variant="default" className="rounded-full" data-testid="notif-push-now-btn">
                <Send className="w-4 h-4 mr-2" aria-hidden="true" /> Enviar push ara
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Enviar push instantani</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Títol</Label><Input value={pushForm.title} onChange={(e) => setPushForm({ ...pushForm, title: e.target.value })} data-testid="push-form-title" /></div>
                <div><Label>Missatge</Label><Textarea value={pushForm.body} onChange={(e) => setPushForm({ ...pushForm, body: e.target.value })} data-testid="push-form-body" /></div>
                <div><Label>Enllaç (opcional)</Label><Input value={pushForm.url} onChange={(e) => setPushForm({ ...pushForm, url: e.target.value })} placeholder="/" data-testid="push-form-url" /></div>
                <div><Label>Destinatari</Label>
                  <Select value={pushForm.target_role} onValueChange={(v) => setPushForm({ ...pushForm, target_role: v })}>
                    <SelectTrigger data-testid="push-form-target"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tothom ({subs.total})</SelectItem>
                      <SelectItem value="associate">Associats ({subs.by_role?.associate || 0})</SelectItem>
                      <SelectItem value="admin">Admin ({subs.by_role?.admin || 0})</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="text-xs text-muted-foreground bg-secondary/60 rounded-lg p-3">
                  Aquest push NO es guarda a l'historial de notificacions. Per crear una entrada permanent, fes servir "Nova notificació".
                </div>
              </div>
              <DialogFooter>
                <Button onClick={broadcast} disabled={sending} data-testid="push-form-save">
                  <Send className="w-4 h-4 mr-2" aria-hidden="true" /> {sending ? "Enviant…" : "Enviar ara"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="rounded-full" variant="outline" data-testid="notif-new-btn"><Plus className="w-4 h-4 mr-2" /> Nova (historial)</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nova notificació</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Títol</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="notif-form-title" /></div>
                <div><Label>Missatge</Label><Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} /></div>
                <div><Label>Destinatari</Label>
                  <Select value={form.target_role} onValueChange={(v) => setForm({ ...form, target_role: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tothom</SelectItem>
                      <SelectItem value="associate">Només associats</SelectItem>
                      <SelectItem value="admin">Només admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Enllaç (opcional)</Label><Input value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} /></div>
                <div className="text-xs text-muted-foreground bg-primary/5 rounded-lg p-3">
                  ⚡ Es guarda a l'historial <strong>i</strong> envia push automàticament als dispositius subscrits del rol seleccionat.
                </div>
              </div>
              <DialogFooter><Button onClick={send} data-testid="notif-form-save">Crear i enviar</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="space-y-3">
        {items.map((n) => (
          <Card key={n.id} className="rounded-2xl border-2" data-testid={`notif-row-${n.id}`}>
            <CardContent className="p-5 flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-accent/15 text-accent flex items-center justify-center"><Bell className="w-4 h-4" aria-hidden="true" /></div>
              <div className="flex-1">
                <div className="font-medium">{n.title}</div>
                <div className="text-sm text-muted-foreground">{n.body}</div>
                <div className="text-xs text-muted-foreground mt-1">{new Date(n.created_at).toLocaleString()} · destinatari: {n.target_role}</div>
              </div>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && <div className="text-muted-foreground text-sm">Cap notificació.</div>}
      </div>
    </div>
  );
}
